import {
  type RowData,
  type ColumnDef,
  flexRender,
  type Table as TanStackTable,
} from "@tanstack/react-table";
import type { ComponentProps } from "react";
import { tv } from "tailwind-variants";

import { EMPTY_STATE_SURFACE_CLASS_NAME } from "./empty-state-styles";
import { m } from "./paraglide/messages";
import type { PlainTableProps } from "./PlainTable";
import {
  PlainTable,
  PlainTableBody,
  PlainTableCell,
  PlainTableHeader,
  PlainTableHeaderCell,
  PlainTableRow,
} from "./PlainTable";
import { ScrollArea } from "./ScrollArea";
import Spinner from "./Spinner";
import { TableEmptyState } from "./TableEmptyState";
import { TableHeaderContent } from "./TableHeaderContent";

const tableStyles = tv({
  slots: {
    container:
      "border-border/50 bg-background-2 flex min-h-0 flex-col overflow-hidden rounded-lg border",
    table: "",
    emptyCell: "py-8 text-center",
  },
  variants: {
    empty: {
      true: {
        container: EMPTY_STATE_SURFACE_CLASS_NAME,
        table:
          "h-full [&>tbody]:h-full [&>tbody>tr]:h-full [&>tbody>tr]:bg-transparent [&>tbody>tr:hover]:bg-transparent",
        emptyCell: "h-full p-0",
      },
    },
  },
});

export type TableHeaderProps<TData extends RowData> = {
  readonly table: TanStackTable<TData>;
} & Pick<PlainTableProps, "bordered" | "sticky" | "roundedBottom" | "color">;

export type TableBodyProps<TData extends RowData> = {
  readonly table: TanStackTable<TData>;
  readonly columns: ColumnDef<TData>[];
  readonly isLoading?: boolean;
  readonly rowHeight?: number;
  readonly emptyState?: Pick<
    ComponentProps<typeof TableEmptyState>,
    "icon" | "title" | "description" | "action"
  >;
} & Pick<PlainTableProps, "bordered" | "sticky" | "roundedBottom" | "color">;

export type TableProps<TData extends RowData> = TableHeaderProps<TData> &
  TableBodyProps<TData> & {
    readonly className?: string;
  };

export function TableHeader<TData extends RowData>(
  props: TableHeaderProps<TData>,
) {
  return (
    <PlainTableHeader>
      {props.table.getHeaderGroups().map((headerGroup) => (
        <PlainTableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <PlainTableHeaderCell
              key={header.id}
              colSpan={header.colSpan}
              style={{
                width: header.getSize(),
              }}
            >
              {header.isPlaceholder ? null : header.column.getCanSort() ? (
                <div className="flex items-center gap-1 select-none">
                  <TableHeaderContent header={header} />
                  {header.column.getIsSorted() && (
                    <button
                      type="button"
                      aria-label="Toggle sorting"
                      className="text-text-secondary cursor-pointer text-lg"
                      onClick={() =>
                        header.column.toggleSorting(
                          header.column.getIsSorted() === "asc",
                        )
                      }
                    >
                      {header.column.getIsSorted() === "asc" ? "↑" : "↓"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <TableHeaderContent header={header} />
                </div>
              )}
            </PlainTableHeaderCell>
          ))}
        </PlainTableRow>
      ))}
    </PlainTableHeader>
  );
}

export function TableBody<TData extends RowData>(props: TableBodyProps<TData>) {
  const rows = getTableRows(props.table);

  const styles = tableStyles({ empty: !props.isLoading && rows.length === 0 });

  const content = props.isLoading ? (
    <PlainTableRow>
      <PlainTableCell
        colSpan={props.columns.length}
        className="py-8 text-center"
      >
        <Spinner size="lg" label={m.loading_spinner()} />
      </PlainTableCell>
    </PlainTableRow>
  ) : rows.length === 0 ? (
    <PlainTableRow>
      <PlainTableCell
        colSpan={props.columns.length}
        className={styles.emptyCell()}
      >
        <TableEmptyState
          variant="content"
          title={m.no_items_found()}
          className="h-full"
          {...props.emptyState}
        />
      </PlainTableCell>
    </PlainTableRow>
  ) : (
    rows.map((row) => (
      <PlainTableRow key={row.id} style={{ height: props.rowHeight }}>
        {row.getVisibleCells().map((cell) => (
          <PlainTableCell
            key={cell.id}
            style={{
              width: `${cell.column.getSize()}px`,
            }}
            className={
              (cell.column.columnDef.meta as { className?: string } | undefined)
                ?.className
            }
            isAction={cell.column.columnDef.id === "actions"}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </PlainTableCell>
        ))}
      </PlainTableRow>
    ))
  );

  return <PlainTableBody>{content}</PlainTableBody>;
}

function Table<TData extends RowData>(props: TableProps<TData>) {
  const hasEmptyState =
    !props.isLoading && getTableRows(props.table).length === 0;

  const styles = tableStyles({ empty: hasEmptyState });

  return (
    <div style={{ contain: "paint" }} className={styles.container()}>
      <ScrollArea orientation="both" className="min-h-0 w-full flex-1">
        <PlainTable
          className={styles.table({ className: props.className })}
          bordered={props.bordered}
          sticky={props.sticky}
          roundedBottom={props.roundedBottom}
          color={props.color}
        >
          {!hasEmptyState && <TableHeader {...props} />}

          <TableBody {...props} />
        </PlainTable>
      </ScrollArea>
    </div>
  );
}

function getTableRows<TData extends RowData>(table: TanStackTable<TData>) {
  return table.options.manualPagination
    ? table.getRowModel().rows
    : table.getPaginationRowModel().rows;
}

export default Table;

export {
  PlainTable,
  PlainTableHeader,
  PlainTableBody,
  PlainTableRow,
  PlainTableCell,
  PlainTableHeaderCell,
} from "./PlainTable";
export type {
  PlainTableProps,
  PlainTableHeaderProps,
  PlainTableBodyProps,
  PlainTableRowProps,
  PlainTableCellProps,
  PlainTableHeaderCellProps,
} from "./PlainTable";
